import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { getAllPMS } from "../../redux/pms/pmsSlice";
import {
  addListingData,
  deleteListingData,
} from "../../redux/admin/adminSlice";
import toast from "react-hot-toast";
import ConfirmDialog from "../../components/ConfirmDialog/ConfirmDialog";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  AddListingButton,
  bodyCellSx,
  DeleteAction,
  DetailInfoGrid,
  EmptyTableRow,
  ExpandButton,
  formatListingDate,
  headCellSx,
  ListingAvatar,
  ListingPageShell,
  PerformanceMiniTable,
  ReturnValue,
  SortableHeader,
} from "../../components/AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT } from "../../constants/layout";

const PMSTableRow = ({ data, onDelete }: { data: any; onDelete: (id: number) => void }) => {
  const [open, setOpen] = useState(false);
  const userType = Number(localStorage.getItem("userType")) || 0;

  const isValidAssetName = (assetName: string) =>
    assetName && assetName !== "-" && assetName.trim() !== "";

  if (!data?.assetName || !isValidAssetName(data.assetName)) return null;

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: "pointer", "&:hover": { bgcolor: "#FAFAFA" } }}
      >
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ListingAvatar name={data.assetName} />
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: DARK_NAVY }}>
                {data.assetName}
              </Typography>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mt: 0.25 }}>
                {data.promoter || "-"}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="center">
          {data.fundManager || "-"}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="center">
          {formatListingDate(data.inceptionDate)}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, color: DARK_NAVY_LIGHT }} align="center">
          {data.aumInCr || "-"}
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ReturnValue value={data.abs1Month} />
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ReturnValue value={data.ann1Year} />
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ReturnValue value={data.returnSinceInception} />
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
            {userType === 4 && <DeleteAction onClick={() => onDelete(data.id)} />}
            <ExpandButton open={open} onClick={() => setOpen(!open)} />
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, borderBottom: open ? `1px solid #F5F5F5` : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
              <DetailInfoGrid
                items={[
                  { label: "Promoter", value: data.promoter },
                  { label: "Fund Manager", value: data.fundManager },
                  { label: "Benchmark", value: data.benchmarkName },
                  { label: "Category", value: data.category || "-" },
                  { label: "AUM (in Cr)", value: data.aumInCr },
                  { label: "Inception Date", value: formatListingDate(data.inceptionDate) },
                  { label: "Exit Load", value: data.exitLoad || "-" },
                  { label: "Fixed Rate", value: data.fixedRate || "-" },
                  { label: "Hybrid Variable Rate", value: data.hybridVariableRate || "-" },
                ]}
              />
              <PerformanceMiniTable
                title="Absolute Returns (%)"
                columns={["1 Month", "3 Months", "6 Months"]}
                rows={[
                  { label: data.assetName, values: [data.abs1Month, data.abs3Month, data.abs6Month] },
                  { label: data.benchmarkName || "Benchmark", values: [data.benchAbs1Month, data.benchAbs3Month, data.benchAbs6Month] },
                ]}
              />
              <PerformanceMiniTable
                title="Annualized Returns (%)"
                columns={["1 Year", "2 Years", "3 Years", "5 Years", "Since Inception"]}
                rows={[
                  {
                    label: data.assetName,
                    values: [data.ann1Year, data.ann2Year, data.ann3Year, data.ann5Year, data.returnSinceInception],
                  },
                  {
                    label: data.benchmarkName || "Benchmark",
                    values: [data.benchAnn1Year, data.benchAnn2Year, data.benchAnn3Year, data.benchAnn5Year, null],
                  },
                ]}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const PMSListing = () => {
  const dispatch = useDispatch<any>();
  const [loader, setLoader] = React.useState(true);
  const userType = Number(localStorage.getItem("userType")) || 0;
  const [totalEnteries, setTotalEnteries] = useState<number>();
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [showAddPMSModal, setShowAddPMSModal] = useState(false);
  const [pmsData, setPMSData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number>(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: string | null;
  }>({
    key: "assetName",
    direction: "ascending",
  });

  const [formData, setFormData] = useState<any>({
    amc: "",
    fundName: "",
    category: "",
    aum: "",
    inceptionDate: "",
    fundManager: "",
    fixed: "",
    Variable: "",
    exitLoad: "",
    largeCap: "",
    midCap: "",
    smallCap: "",
    cash: "",
    oneM: "",
    threeM: "",
    sixM: "",
    oneY: "",
    twoY: "",
    threeY: "",
    fiveY: "",
    rsi: "",
    alpha: "",
    beta: "",
    sd: "",
    upCapture: "",
    downCapture: "",
    sharpRatio: "",
    sortino: "",
  });

  //Fetch MF Data from Get All fund list API
  const fetchData = async () => {
    setLoader(true); // Start loading
    try {
      const response = await dispatch(
        getAllPMS({ pageIndex: 1, pageSize: 1000 })
      );

      if (response) {
        let pmsList = response?.payload?.pmsList || [];
        setTotalEnteries(pmsList.length);
        setPMSData(pmsList);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setLoader(false); // End loading
      //   setfilterButton(false);
    }
  };

  useEffect(() => {
    setLoader(true);
    fetchData();
  }, []);

  useEffect(() => {
    if (searchInput === "") {
      setFilteredData(pmsData);
    } else {
      const lowercasedInput = searchInput.toLowerCase();
      const newFilteredData = pmsData.filter(
        (entry: any) =>
          entry.assetName.toLowerCase()?.includes(lowercasedInput) ||
          entry.promoter.toLowerCase()?.includes(lowercasedInput) ||
          entry.category.toLowerCase()?.includes(lowercasedInput)
      );
      setFilteredData(newFilteredData);
    }
  }, [searchInput, pmsData]);

  const resetState = () => {
    setFormData({
      amc: "",
      fundName: "",
      category: "",
      aum: "",
      inceptionDate: "",
      fundManager: "",
      fixed: "",
      Variable: "",
      exitLoad: "",
      largeCap: "",
      midCap: "",
      smallCap: "",
      cash: "",
      oneM: "",
      threeM: "",
      sixM: "",
      oneY: "",
      twoY: "",
      threeY: "",
      fiveY: "",
      rsi: "",
      alpha: "",
      beta: "",
      sd: "",
      upCapture: "",
      downCapture: "",
      sharpRatio: "",
      sortino: "",
    });
    setShowAddPMSModal(false);
  };

  const getSortedData = useCallback(() => {
    if (!sortConfig.key) return filteredData;

    const sortedData = [...filteredData];

    switch (sortConfig.key) {
      case "assetName":
        sortedData.sort((a, b) => {
          const nameA = (a.assetName || "").toUpperCase();
          const nameB = (b.assetName || "").toUpperCase();
          return (
            (nameA < nameB ? -1 : nameA > nameB ? 1 : 0) *
            (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      case "promoter":
        sortedData.sort((a, b) => {
          const nameA = (a.promoter || "").toUpperCase();
          const nameB = (b.promoter || "").toUpperCase();
          return (
            (nameA < nameB ? -1 : nameA > nameB ? 1 : 0) *
            (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      case "inceptionDate":
        sortedData.sort((a, b) => {
          const dateA = new Date(a.inceptionDate || "").getTime();
          const dateB = new Date(b.inceptionDate || "").getTime();
          return (
            (dateA - dateB) * (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;
      case "aumInCr":
      case "abs1Month":
      case "ann1Year":
      case "returnSinceInception":
        sortedData.sort((a, b) => {
          const valueA = parseFloat(a[sortConfig.key]) || 0;
          const valueB = parseFloat(b[sortConfig.key]) || 0;
          return (
            (valueA - valueB) * (sortConfig.direction === "ascending" ? 1 : -1)
          );
        });
        break;

      default:
        break;
    }

    return sortedData;
  }, [filteredData, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction:
        prev.key === key && prev.direction === "ascending"
          ? "descending"
          : "ascending",
    }));
  };

  const sortedData = getSortedData();

  const handleAddPMS = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        addListingData({
          listingType: 6,
          ...formData,
        })
      );
      if (response?.error?.message) {
        toast.error("Error adding stock.");
        return;
      }

      toast.success("Stock added successfully.");
      fetchData();
      resetState();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  const onDelete = (id: number) => {
    setShowDeleteModal(true);
    setDeleteId(id);
  };

  const handleDeletePMS = async () => {
    try {
      setLoader(true);
      const response = await dispatch(
        deleteListingData({
          listingType: 6,
          id: deleteId,
        })
      );
      if (response?.error?.message) {
        toast.error("Error deleting PMS.");
        return;
      }

      toast.success("PMS deleted successfully.");
      setShowDeleteModal(false);
      fetchData();
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <AppLoader loading={loader} />
      <ListingPageShell
        title="Portfolio Management Services (PMS)"
        subtitle="Browse and manage PMS schemes with performance and key details."
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        searchPlaceholder="Search PMS by name, promoter or category..."
        action={
          userType === 4 ? (
            <AddListingButton label="Add PMS" onClick={() => setShowAddPMSModal(true)} />
          ) : undefined
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>
                  <SortableHeader label="Scheme" onSort={() => handleSort("assetName")} />
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  Fund Manager
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  <SortableHeader label="Inception" onSort={() => handleSort("inceptionDate")} align="center" />
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  <SortableHeader label="AUM (Cr)" onSort={() => handleSort("aumInCr")} align="center" />
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  <SortableHeader label="1M Abs." onSort={() => handleSort("abs1Month")} align="center" />
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  <SortableHeader label="1Y Ann." onSort={() => handleSort("ann1Year")} align="center" />
                </TableCell>
                <TableCell sx={headCellSx} align="center">
                  <SortableHeader label="Since Inception" onSort={() => handleSort("returnSinceInception")} align="center" />
                </TableCell>
                <TableCell sx={{ ...headCellSx, width: 88 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.length === 0 ? (
                <TableRow>
                  <EmptyTableRow colSpan={8} />
                </TableRow>
              ) : (
                sortedData.map((row: any, index: number) => (
                  <PMSTableRow key={row.id ?? index} data={row} onDelete={onDelete} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ListingPageShell>
      <Modal open={showAddPMSModal} onClose={() => setShowAddPMSModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 800, // Adjust width as needed
            maxHeight: "80vh", // Limit the height of the modal
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
            overflowY: "auto", // Make the content scrollable on the Y-axis
            borderRadius: 2,
          }}
        >
          <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" component="h2">
              {"Add PMS"}
            </Typography>
            <IconButton onClick={() => setShowAddPMSModal(false)}>
              <Close />
            </IconButton>
          </Box>
          <Box component="form" noValidate autoComplete="off">
            <div className="grid grid-cols-2 gap-4">
              {/* <TextField
                label="AMC Name *"
                variant="outlined"
                fullWidth
                margin="normal"
                name="amc"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, amc: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.amc || ""}
              /> */}
              <TextField
                label="Scheme Name *"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fundName"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fundName: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fundName || ""}
              />
              <TextField
                label="Category *"
                variant="outlined"
                fullWidth
                margin="normal"
                name="category"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.category || ""}
              />
              <TextField
                label="AUM (in Cr)"
                variant="outlined"
                fullWidth
                margin="normal"
                name="aum"
                type="number"
                onChange={(e) =>
                  setFormData({ ...formData, aum: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.aum || ""}
              />
              <TextField
                label="Inception Date"
                variant="outlined"
                fullWidth
                margin="normal"
                name="inceptionDate"
                type="date"
                onChange={(e) =>
                  setFormData({ ...formData, inceptionDate: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.inceptionDate || ""}
              />
              <TextField
                label="Fund Manager"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fundManager"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fundManager: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fundManager || ""}
              />
              <TextField
                label="Fixed Rate"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fixed"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fixed: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fixed || ""}
              />
              <TextField
                label="Hybrid Variable Rate"
                variant="outlined"
                fullWidth
                margin="normal"
                name="variable"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, variable: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.variable || ""}
              />
              <TextField
                label="Exit Load"
                variant="outlined"
                fullWidth
                margin="normal"
                name="exitLoad"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, exitLoad: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.exitLoad || ""}
              />
              <TextField
                label="Large Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="largeCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, largeCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.largeCap || ""}
              />
              <TextField
                label="Mid Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="midCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, midCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.midCap || ""}
              />
              <TextField
                label="Small Cap"
                variant="outlined"
                fullWidth
                margin="normal"
                name="smallCap"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, smallCap: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.smallCap || ""}
              />
              <TextField
                label="Cash"
                variant="outlined"
                fullWidth
                margin="normal"
                name="cash"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, cash: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.cash || ""}
              />
              <TextField
                label="1M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="oneM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, oneM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.oneM || ""}
              />
              <TextField
                label="3M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="threeM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, threeM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.threeM || ""}
              />
              <TextField
                label="6M Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sixM"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sixM: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sixM || ""}
              />
              <TextField
                label="1Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="oneY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, oneY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.oneY || ""}
              />
              <TextField
                label="2Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="twoY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, twoY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.twoY || ""}
              />
              <TextField
                label="3Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="threeY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, threeY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.threeY || ""}
              />
              <TextField
                label="5Y Return"
                variant="outlined"
                fullWidth
                margin="normal"
                name="fiveY"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, fiveY: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.fiveY || ""}
              />
              <TextField
                label="Return Since Inception"
                variant="outlined"
                fullWidth
                margin="normal"
                name="rsi"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, rsi: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.rsi || ""}
              />
              <TextField
                label="Alpha Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="alpha"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, alpha: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.alpha || ""}
              />
              <TextField
                label="Beta Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="beta"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, beta: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.beta || ""}
              />
              <TextField
                label="SD Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sd"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sd: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sd || ""}
              />
              <TextField
                label="Up Capture Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="upCapture"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, upCapture: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.upCapture || ""}
              />
              <TextField
                label="Down Capture Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="downCapture"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, downCapture: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.downCapture || ""}
              />
              <TextField
                label="Sharp Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sharpRatio"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sharpRatio: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sharpRatio || ""}
              />
              <TextField
                label="Sortino Ratio"
                variant="outlined"
                fullWidth
                margin="normal"
                name="sortino"
                type="text"
                onChange={(e) =>
                  setFormData({ ...formData, sortino: e.target.value })
                }
                InputLabelProps={{ shrink: true }}
                value={formData?.sortino || ""}
              />
            </div>
            <div className="mt-4">
              <Button
                onClick={handleAddPMS}
                color="primary"
                variant="contained"
                sx={{
                  mr: 1,
                }}
                disabled={!formData.fundName || !formData.category}
              >
                Submit
              </Button>
            </div>
          </Box>
        </Box>
      </Modal>
      <ConfirmDialog
        title="Delete PMS"
        message="Are you sure you want to delete this PMS?"
        showDeleteModal={showDeleteModal}
        setShowDeleteModal={setShowDeleteModal}
        onDelete={handleDeletePMS}
      />
    </>
  );
};

export default PMSListing;
